import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, keys, useMe } from '../../api.ts';
import { formatBand, formatDate } from '../../../shared/format.ts';
import type { CompetitionDetail, TableReport } from '../../../shared/types.ts';
import { ReportTable } from '../../components/ReportTable.tsx';
import { ConfirmButton, ErrorBox, Loading, PageHeader, useDocumentTitle, useToast } from '../../components/ui.tsx';

export function useCompetition(id: number) {
  return useQuery({
    queryKey: keys.competition(id),
    queryFn: () => api.get<{ competition: CompetitionDetail; results: TableReport }>(`/competitions/${id}`),
    enabled: id > 0,
  });
}

export function RulesSummary({ c }: { c: CompetitionDetail }) {
  const r = c.rules;
  const items: string[] = [];
  if (r.licenceMinYears !== null) items.push(`lisenssi vähintään ${r.licenceMinYears} v`);
  if (r.licenceMaxYears !== null) items.push(`lisenssi alle ${r.licenceMaxYears} v`);
  if (r.classes.length) items.push(r.classes.join(' / '));
  if (r.genres.length) items.push(r.genres.join(' / '));
  if (r.bands.length) items.push(`bandit ${r.bands.map(formatBand).join(', ')}`);
  if (r.modes.length) items.push(`moodit ${r.modes.join(', ')}`);
  if (r.countries.length) items.push(`${r.countries.length} valittua maata`);
  return (
    <div className="muted small">
      Voimassa {formatDate(c.startDate)} – {formatDate(c.endDate)}
      {items.length > 0 && <> · {items.join(' · ')}</>}
    </div>
  );
}

export function CompetitionPage() {
  const id = Number(useParams().id);
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useCompetition(id);
  useDocumentTitle(q.data?.competition.heading);
  const removeOwn = useMutation({
    mutationFn: () => api.del(`/competitions/${id}/entries`),
    onSuccess: () => {
      toast.ok('Tuloksesi on poistettu');
      void qc.invalidateQueries({ queryKey: keys.competition(id) });
    },
    onError: toast.error,
  });

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const { competition: c, results } = q.data;
  const participating = !!me && results.rows.some((r) => String(r.userId).toUpperCase() === me.id.toUpperCase());

  return (
    <div className="page">
      <PageHeader
        title={c.heading}
        sub={<RulesSummary c={c} />}
        actions={
          <>
            {me && c.isOpen && (
              <Link className="btn btn-primary" to={`/competitions/${id}/participate`}>
                {participating ? 'Lisää QSO:ita' : 'Osallistu'}
              </Link>
            )}
            {participating && (
              <ConfirmButton title="Poista tulokset" message="Haluatko varmasti poistaa omat tuloksesi tästä kilpailusta?" confirmText="Poista" onConfirm={() => removeOwn.mutateAsync()}>
                Poista omat tulokset
              </ConfirmButton>
            )}
            {me?.isAdmin && (
              <Link className="btn" to={`/competitions/${id}/edit`}>
                Muokkaa
              </Link>
            )}
          </>
        }
      />
      <div className="card" style={{ whiteSpace: 'pre-wrap', marginBottom: 20 }}>
        {c.description}
      </div>
      <h2>Tulokset</h2>
      <ReportTable data={results} filters={null} linkCells={false} meId={me?.id} />
    </div>
  );
}
